import type { OfferDto, OfferTriggerEvent, OfferTriggerEventRequest } from '../api/dto/offers.dto.js';
import { reportOfferTriggerEvent } from '../api/requests/offers.requests.js';
import { DialogManager } from '../ui/dialog/dialog_manager.js';
import { OverlayManager } from '../ui/overlay/overlay_manager.js';
import { OfferService } from './offer_service.js';
import { presentDecisionOffer } from './placement_offer_registry.js';
import { GlobalSettings } from '../global_settings.js';

type PendingOffer = { offer: OfferDto; trigger_event_id: string };
const OFFER_SESSION_ID = globalThis.crypto?.randomUUID?.()
  ?? `offer_session_${Date.now()}_${Math.random().toString( 36 ).slice( 2 )}`;

/** Serializes remotely eligible dialogs so gameplay/system dialogs stay on top. */
export const OfferTriggerController = {
  pending: [] as PendingOffer[],
  drain_timer: null as ReturnType<typeof setTimeout> | null,

  async report( event: OfferTriggerEvent, options: Omit<OfferTriggerEventRequest, 'event' | 'event_id' | 'occurred_at'> = {} ): Promise<void>
  {
    if ( !GlobalSettings.PROMO_OFFERS_ENABLED ) return;

    const event_id = globalThis.crypto?.randomUUID?.()
      ?? `offer_${event}_${Date.now()}_${Math.random().toString( 36 ).slice( 2 )}`;
    try
    {
      const response = await reportOfferTriggerEvent( {
        event_id,
        event,
        session_id: OFFER_SESSION_ID,
        occurred_at: new Date().toISOString(),
        ...options,
      } );
      const offer = response.decision?.action === 'show_offer' ? response.decision.offer : undefined;
      if ( offer )
      {
        OfferService.rememberOffer( offer );
        // A panel inside an already open screen (Continue) must not wait for
        // "no dialog is open" — that screen is the one waiting for it.
        if ( offer.placement?.template === 'continue_special_offer' )
        {
          presentDecisionOffer( offer, response.event_id );
          return;
        }
        this.pending.push( { offer, trigger_event_id: response.event_id } );
        this.drain();
      }
    }
    catch ( error )
    {
      console.warn( '[OfferTrigger] event report failed', event, error );
    }
  },

  drain(): void
  {
    if ( this.drain_timer ) return;
    const tryOpen = (): void =>
    {
      this.drain_timer = null;
      if ( DialogManager.isAnyDialogOpened() || OverlayManager.isAnyOverlayOpened() )
      {
        this.drain_timer = setTimeout( tryOpen, 350 );
        return;
      }
      const next = this.pending.shift();
      if ( !next ) return;
      presentDecisionOffer( next.offer, next.trigger_event_id );
      this.drain_timer = setTimeout( tryOpen, 500 );
    };
    tryOpen();
  },
};
